export type VisitorPreferenceStorage = Pick<Storage, "getItem" | "setItem">;

export const VISITOR_BACKGROUND_MUSIC_MUTED_KEY = "artetra.visitor.backgroundMusicMuted";
export const FLASHING_EFFECTS_ENABLED_KEY = "artetra.visitor.flashingEffectsEnabled";

const getBrowserStorage = (): VisitorPreferenceStorage | undefined => {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch (_error) {
    return undefined;
  }
};

const prefersReducedMotion = (): boolean => {
  try {
    return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
  } catch (_error) {
    return false;
  }
};

const readBoolean = (
  key: string,
  fallback: boolean,
  storage?: VisitorPreferenceStorage
): boolean => {
  try {
    const stored = storage?.getItem(key);
    if (stored === "true") return true;
    if (stored === "false") return false;
  } catch (_error) {
    return fallback;
  }
  return fallback;
};

const writeBoolean = (
  key: string,
  value: boolean,
  storage?: VisitorPreferenceStorage
) => {
  try {
    storage?.setItem(key, String(value));
  } catch (_error) {
    // The current page still uses the in-memory preference when storage is unavailable.
  }
};

export const getVisitorBackgroundMusicMuted = (
  storage: VisitorPreferenceStorage | undefined = getBrowserStorage()
): boolean => readBoolean(VISITOR_BACKGROUND_MUSIC_MUTED_KEY, false, storage);

export const setVisitorBackgroundMusicMuted = (
  muted: boolean,
  storage: VisitorPreferenceStorage | undefined = getBrowserStorage()
) => writeBoolean(VISITOR_BACKGROUND_MUSIC_MUTED_KEY, muted, storage);

export const isGalleryBackgroundMusicPlayable = (sound?: { url?: string; muted?: boolean } | null): boolean =>
  Boolean(sound?.url?.trim()) && !Boolean(sound?.muted);

export const getFlashingEffectsEnabled = (
  storage: VisitorPreferenceStorage | undefined = getBrowserStorage(),
  reducedMotion: boolean = prefersReducedMotion()
): boolean => readBoolean(FLASHING_EFFECTS_ENABLED_KEY, !reducedMotion, storage);

export const setFlashingEffectsEnabled = (
  enabled: boolean,
  storage: VisitorPreferenceStorage | undefined = getBrowserStorage()
) => writeBoolean(FLASHING_EFFECTS_ENABLED_KEY, enabled, storage);
